import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/esm/Button";
import Col from "react-bootstrap/esm/Col";
import Container from "react-bootstrap/esm/Container";
import Row from "react-bootstrap/esm/Row";
import OrHR from "../../components/orHR";
import { onetimePassword } from "../../helpers/onetimePassword";
import EditBIACode, {
  BIACodeValues,
} from "../../components/formControls/editBIACode";
import EditContact, {
  ContactValues,
} from "../../components/formControls/editContact";
import EditLicenses, {
  LicenseValues,
} from "../../components/formControls/editLicenses";
import EditPersonalInformation, {
  PersonalInformationValues,
} from "../../components/formControls/editPersonalInformation";
import passwordReg from "../../helpers/passwordReg";
import { createFisher, Fisher } from "../../api/createFisher";
import { usernameReg } from "../../helpers/usernameReg";
import { getOrganizationID } from "../../helpers/authHelper";
import { removeEmptyValues } from "../../helpers/removeEmptyValues";
import FormToast, { ToastData } from "../../components/formToast";
import { useNavigate } from "react-router-dom";
import { apiSuccess } from "../../helpers/apiSuccess";
import { lookupOrganization } from "../../api/lookupOrganization";
import { Organization } from "../../models/organization";

export default function RegisterFisher() {
  interface FormData {
    values: any;
    validated: boolean;
    errors: any;
  }

  interface RegisterFisher {
    username: string;
    oneTimePassword: string;
  }

  const [org, setOrg] = useState<Organization>();

  // Toast notification and Navigation
  const navigate = useNavigate();
  const [disabled, setDisabled] = useState(false);
  const [toastData, setToastData] = useState<ToastData>({
    show: false,
    title: "",
    body: "",
    bg: "",
  });

  // Set initial values
  const personalInformationValues: PersonalInformationValues = {
    firstName: "",
    middleName: "",
    lastName: "",
    address1: "",
    city: "",
    stateCode: "",
    zip: "",
  };

  const biaCodeValues: BIACodeValues = {
    organizationID: getOrganizationID() === 1 ? 2 : getOrganizationID(),
    biaNumber: "",
    organizationCodeNumber: "",
  };

  const contactValues: ContactValues = {
    email: "",
    secondaryEmail: "",
    phone: "",
    secondaryPhone: "",
  };

  const licenseValues: LicenseValues = {
    commercialLicenseNumber: "",
    commercialYear: "",
    subsistenceLicenseNumber: "",
    subsistenceYear: "",
  };

  const registerFisherValues: RegisterFisher = {
    username: "",
    oneTimePassword: "",
  };

  // Set all forms in single object
  const [formData, setFormData] = React.useState({
    editPersonalInformation: {
      values: personalInformationValues,
      validated: false,
    },
    editBIACode: { values: biaCodeValues, validated: false, errors: {} },
    editContact: { values: contactValues, validated: false, errors: {} },
    editLicenses: { values: licenseValues, validated: false, errors: {} },
    editRegisterFisher: {
      values: registerFisherValues,
      validated: false,
      errors: {},
    },
  });

  useEffect(() => {
    const fetchOrg = async () => {
      const response = await lookupOrganization(
        formData.editBIACode.values.organizationID
      );
      if (response) setOrg(response);
    };
    fetchOrg();
  }, [formData.editBIACode.values.organizationID]);

  // Create Refs for each form
  const editPersonalInformationRef = React.useRef<HTMLFormElement>(null);
  const editBIACodeRef = React.useRef<HTMLFormElement>(null);
  const editContactRef = React.useRef<HTMLFormElement>(null);
  const editLicensesRef = React.useRef<HTMLFormElement>(null);

  // Handle form data when updated
  const handleChangeEditPersonalInformation = (data: FormData) => {
    formData.editPersonalInformation = { ...data };
    setFormData({ ...formData });
  };

  const handleChangeEditBIACode = (data: FormData) => {
    formData.editBIACode = { ...data };
    setFormData({ ...formData });
  };

  const handleChangeEditContact = (data: FormData) => {
    formData.editContact = { ...data };
    setFormData({ ...formData });
  };

  const handleChangeEditLicenses = (data: FormData) => {
    formData.editLicenses = { ...data };
    setFormData({ ...formData });
  };

  const handleChangeEditRegisterFisher = (data: FormData) => {
    formData.editRegisterFisher = { ...data };
    setFormData({ ...formData });
  };

  // Register with username and password
  async function registerFisherUsernamePassword() {
    try {
      await editPersonalInformationRef.current?.Submit();
      await editBIACodeRef.current?.Submit();
      await editContactRef.current?.Submit();
      await editLicensesRef.current?.Submit();
      await formik.submitForm();
      if (
        formData.editPersonalInformation.validated &&
        formData.editBIACode.validated &&
        formData.editContact.validated &&
        formData.editLicenses.validated &&
        formData.editRegisterFisher.validated
      ) {
        const data: Fisher = {
          ...formData.editPersonalInformation.values,
          ...formData.editBIACode.values,
          ...formData.editContact.values,
          ...formData.editLicenses.values,
          ...formData.editRegisterFisher.values,
          createVia: "PASSWORD",
        };

        removeEmptyValues(data);
        registerFisher(data);
      } else {
        // Form Validation Errors
        setToastData({
          show: true,
          bg: "Danger",
          title: "Error",
          body: "There were validation errors",
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  // Register Fisher with Email Link
  async function submitEmailRegistration() {
    await editPersonalInformationRef.current?.Submit();
    await editBIACodeRef.current?.Submit();
    await editContactRef.current?.Submit();
    await editLicensesRef.current?.Submit();

    if (
      formData.editPersonalInformation.validated &&
      formData.editBIACode.validated &&
      formData.editContact.validated &&
      formData.editLicenses.validated
    ) {
      const data: Fisher = {
        ...formData.editPersonalInformation.values,
        ...formData.editBIACode.values,
        ...formData.editContact.values,
        ...formData.editLicenses.values,
        ...formData.editRegisterFisher.values,
        createVia: "EMAIL",
      };

      removeEmptyValues(data);
      registerFisher(data);
    } else {
      // Form Validation Errors
      setToastData({
        show: true,
        bg: "Danger",
        title: "Error",
        body: "There were validation errors",
      });
    }
  }

  // Register Fisher with Phone Link
  async function submitPhoneRegistration() {
    await editPersonalInformationRef.current?.Submit();
    await editBIACodeRef.current?.Submit();
    await editContactRef.current?.Submit();
    await editLicensesRef.current?.Submit();

    if (
      formData.editPersonalInformation.validated &&
      formData.editBIACode.validated &&
      formData.editContact.validated &&
      formData.editLicenses.validated
    ) {
      const data: Fisher = {
        ...formData.editPersonalInformation.values,
        ...formData.editBIACode.values,
        ...formData.editContact.values,
        ...formData.editLicenses.values,
        ...formData.editRegisterFisher.values,
        createVia: "PHONE",
      };

      removeEmptyValues(data);
      registerFisher(data);
    } else {
      // Form Validation Errors
      setToastData({
        show: true,
        bg: "Danger",
        title: "Error",
        body: "There were validation errors",
      });
    }
  }

  // API Call to register Fisher
  const registerFisher = async (data: Fisher) => {
    const response = await createFisher(data);

    // console.log("FISHER", data);

    // Reset Form
    if (apiSuccess(response.status)) {
      setToastData({
        show: true,
        bg: "Success",
        title: "Success",
        body: "Fisher registered",
      });
      setDisabled(true);
      setTimeout(() => navigate("/fishers"), 3000);
    }
  };

  // Register Fisher Form
  const formik = useFormik({
    initialValues: {
      username: "",
      oneTimePassword: "",
    },
    validationSchema: Yup.object().shape({
      username: Yup.string()
        .min(6, "Username must be 6 characters or longer")
        .matches(usernameReg, "Only letters and numbers allowed")
        .required("Username is required"),
      oneTimePassword: Yup.string()
        .required("Password required")
        .min(10, "Minimum ten characters, at least one letter and one number")
        .test(
          "isValidPass",
          "At least one letter and one number",
          (value: any, context) => {
            return passwordReg(value, context);
          }
        ),
    }),
    onSubmit: () => {
      handleChangeEditRegisterFisher({
        values: formik.values,
        validated:
          formik.dirty && Object.keys(formik.errors).length === 0
            ? true
            : false,
        errors: formik.errors,
      });
    },
  });

  // Update values on change
  React.useEffect(() => {
    handleChangeEditRegisterFisher({
      values: formik.values,
      validated:
        formik.dirty && Object.keys(formik.errors).length === 0 ? true : false,
      errors: formik.errors,
    });
  }, [formik.values, formik.errors]);

  // Generate Password
  const generatePassword = () => {
    formik.setFieldValue("oneTimePassword", onetimePassword());
  };

  return (
    <div className="py-5">
      <Container>
        <FormToast toastData={toastData} setData={setToastData} />

        <h1 className="text-primary mb-5">Register New Fisher</h1>

        {/* Personal Information */}
        <Row>
          <Col lg={3}>
            <h2 className="form-header">Personal Information</h2>
          </Col>
          <Col>
            <EditPersonalInformation
              formData={handleChangeEditPersonalInformation}
              refId={editPersonalInformationRef}
              initialValues={personalInformationValues}
            />
          </Col>
        </Row>

        {/* BIA & Code */}
        <Row>
          <Col lg={3}>
            <h2 className="form-header">Tribe &amp; Code</h2>
          </Col>
          <Col>
            <EditBIACode
              formData={handleChangeEditBIACode}
              refId={editBIACodeRef}
              initialValues={biaCodeValues}
              allowChangeTribe={getOrganizationID() === 1}
              includeCORA={false}
            />
          </Col>
        </Row>

        {/* Contact Details */}
        <Row>
          <Col lg={3}>
            <h2 className="form-header">Contact Details</h2>
          </Col>
          <Col>
            <EditContact
              formData={handleChangeEditContact}
              refId={editContactRef}
              initialValues={contactValues}
            />
          </Col>
        </Row>

        {/* Licenses */}
        <Row>
          <Col lg={3}>
            <h2 className="form-header">Licenses</h2>
          </Col>
          <Col>
            <EditLicenses
              formData={handleChangeEditLicenses}
              refId={editLicensesRef}
              initialValues={licenseValues}
              organization={org}
            />
          </Col>
        </Row>

        {/* Register Fisher */}
        <Row>
          <Col lg={3}>
            <h2 className="form-header">Register Fisher</h2>
          </Col>
          <Col>
            <div className="form-card callout-success">
              <Form
                id="register-fisher-form"
                noValidate
                onSubmit={formik.handleSubmit}
                autoComplete="off"
              >
                <Row>
                  <Form.Group
                    as={Col}
                    xl
                    className="mb-3"
                    controlId="formUsername"
                  >
                    <Form.Label>Username*</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={formik.values.username}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        formik.touched.username && !!formik.errors.username
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.username}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group
                    as={Col}
                    className="mb-3"
                    controlId="formNewAdminPassword"
                  >
                    <Form.Label>One-time Password*</Form.Label>
                    <Form.Control
                      type="text"
                      name="oneTimePassword"
                      value={formik.values.oneTimePassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        formik.touched.oneTimePassword &&
                        !!formik.errors.oneTimePassword
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.oneTimePassword}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Col xs="auto" xl={4}>
                    <Button
                      onClick={generatePassword}
                      variant="secondary"
                      className="w-100 form-btn mb-3"
                    >
                      Generate Password
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      variant="success"
                      className="w-100 mb-3"
                      onClick={registerFisherUsernamePassword}
                      disabled={disabled}
                    >
                      Register Fisher
                    </Button>
                    <p className="mb-0">
                      Create account with a one-time password.
                    </p>
                  </Col>
                </Row>
                <OrHR />

                <Row>
                  <Col md>
                    <Button
                      variant="primary"
                      className="w-100 mb-3"
                      onClick={submitEmailRegistration}
                      disabled={
                        formData.editContact.errors?.hasOwnProperty("email") ||
                        !formData.editContact.validated ||
                        !formData.editContact.values.email ||
                        disabled
                      }
                    >
                      Email Registration Link
                    </Button>
                  </Col>
                  <Col md>
                    <Button
                      variant="primary"
                      className="w-100 mb-3"
                      onClick={submitPhoneRegistration}
                      disabled={
                        formData.editContact.errors?.hasOwnProperty("phone") ||
                        !formData.editContact.validated ||
                        !formData.editContact.values.phone ||
                        disabled
                      }
                    >
                      Text Registration Link
                    </Button>
                  </Col>
                </Row>
                <p>
                  Send a registration link to the fisher to finish account
                  registration.
                  <br />
                  Account will be created with a status of <strong>New</strong>
                </p>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
